/**
 * RailedLayout — page-level 3-column layout used by Sourcing AND SCM/SRM pages.
 *
 * iter-143: generalised from SourcingRailedLayout. Accepts a `moduleKey`
 * (the key into /config/moduleLinks.js) and auto-renders the canonical
 * QuickLinksPanel in the left rail. Pages still portal their HowItWorks +
 * AI Agent panel via RailPortal — and the AppLayout wraps every module
 * path with this layout (no per-page edits needed).
 */
import { useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useRailsCollapsed, COLLAPSED_WIDTH } from '@/hooks/useRailsCollapsed';
import { RailResizeHandle } from '@/components/layout/RailResizeHandle';
import { QuickLinksPanel } from '@/components/QuickLinksPanel';
import { getModuleLinks } from '@/config/moduleLinks';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const RailsCtx = createContext(null);
export { RailsCtx };

export function RailedLayout({ moduleKey, children }) {
  const [leftNode, setLeftNode] = useState(null);
  const [rightNode, setRightNode] = useState(null);
  const rails = useRailsCollapsed();
  const { leftCollapsed, rightCollapsed, toggleLeft, toggleRight,
          leftWidth, rightWidth, setLeftWidth, setRightWidth } = rails;
  const hasQL = moduleKey && !!getModuleLinks(moduleKey);

  return (
    <RailsCtx.Provider value={{ leftNode, rightNode, ready: true, hasContent: hasQL }}>
      <div className="py-3 px-3 max-w-none">
        <div className="flex gap-3 items-start"
             data-testid="rails-layout"
             data-module-key={moduleKey || ''}
             data-left-collapsed={leftCollapsed ? '1' : '0'}
             data-right-collapsed={rightCollapsed ? '1' : '0'}
             data-left-width={leftCollapsed ? COLLAPSED_WIDTH : leftWidth}
             data-right-width={rightCollapsed ? COLLAPSED_WIDTH : rightWidth}>
          <RailWrapper side="left"
                       collapsed={leftCollapsed}
                       width={leftCollapsed ? COLLAPSED_WIDTH : leftWidth}
                       onToggle={toggleLeft}
                       onResize={setLeftWidth}
                       refCallback={setLeftNode}
                       testId="rails-left"
                       header={hasQL && <QuickLinksPanel moduleKey={moduleKey} _railManaged />}/>
          <main className="flex-1 min-w-0" data-testid="rails-main">
            {children}
          </main>
          <RailWrapper side="right"
                       collapsed={rightCollapsed}
                       width={rightCollapsed ? COLLAPSED_WIDTH : rightWidth}
                       onToggle={toggleRight}
                       onResize={setRightWidth}
                       refCallback={setRightNode}
                       testId="rails-right"/>
        </div>
      </div>
    </RailsCtx.Provider>
  );
}

/** Single rail column with chevron + resize handle + portal-target div. */
function RailWrapper({ side, collapsed, width, onToggle, onResize, refCallback, testId, header }) {
  const isLeft = side === 'left';
  const ChevronCollapsed = isLeft ? ChevronRight : ChevronLeft;
  const ChevronExpanded = isLeft ? ChevronLeft : ChevronRight;
  const shortcut = isLeft ? 'Ctrl + [' : 'Ctrl + ]';

  if (collapsed) {
    return (
      <aside className="hidden lg:flex lg:flex-col shrink-0 group/rail"
             style={{ width: `${width}px` }}
             data-testid={`rail-${side}-collapsed`}>
        <div className="sticky top-3 self-start max-h-[calc(100vh-5rem)] relative w-9">
          <button
            type="button"
            onClick={onToggle}
            title={`Expand ${side} rail (${shortcut})`}
            data-testid={`rail-${side}-toggle`}
            className="w-9 h-32 rounded-lg border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 transition-colors flex flex-col items-center justify-between py-2 cursor-pointer select-none"
          >
            <ChevronCollapsed className="w-3.5 h-3.5 text-emerald-700"/>
            <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                  className="text-[9px] font-semibold tracking-wider text-emerald-800/80">
              {isLeft ? 'Q U I C K  L I N K S  /  H O W' : 'A I   A G E N T'}
            </span>
            <ChevronCollapsed className="w-3.5 h-3.5 text-emerald-700 opacity-0"/>
          </button>
          <div className={`absolute top-0 ${isLeft ? 'left-10' : 'right-10'} w-80 max-h-[calc(100vh-5rem)] overflow-y-auto opacity-0 invisible group-hover/rail:opacity-100 group-hover/rail:visible transition-all duration-150 z-30 bg-white border border-slate-200 rounded-lg shadow-xl p-2 space-y-2`}
               data-testid={`rail-${side}-drawer`}>
            {header}
            <div ref={refCallback} data-testid={testId} />
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`hidden lg:flex lg:flex-col shrink-0 sticky top-3 self-start max-h-[calc(100vh-5rem)] overflow-y-auto relative ${isLeft ? 'pr-1' : 'pl-1'}`}
           style={{ width: `${width}px` }}
           data-testid={`rail-${side}-expanded`}>
      <div className={`flex items-center justify-between gap-1 mb-1 ${isLeft ? '' : 'flex-row-reverse'}`}>
        <button
          type="button"
          onClick={onToggle}
          title={`Collapse ${side} rail (${shortcut})`}
          data-testid={`rail-${side}-toggle`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
        >
          <ChevronExpanded className="w-3 h-3"/>
          <span className="font-medium">Collapse</span>
        </button>
        <span className="text-[9px] text-slate-400 font-mono">{width} px</span>
      </div>
      {header}
      <div className="space-y-3 flex-1 min-h-0" ref={refCallback} data-testid={testId} />
      <RailResizeHandle side={side} currentWidth={width} onResize={onResize} disabled={false}/>
    </aside>
  );
}

export function useRails() {
  return useContext(RailsCtx);
}

export function RailPortal({ side, children }) {
  const ctx = useRails();
  if (!ctx) return null;
  const target = side === 'left' ? ctx.leftNode : ctx.rightNode;
  if (!target) return null;
  return createPortal(children, target);
}

// Back-compat: old name used by SourcingL2Header
export const SourcingRailedLayout = RailedLayout;
export const useSourcingRails = useRails;
